import * as d3 from 'd3';
import * as _ from 'lodash';
import * as React from 'react';
import cx from 'classnames';
import moment from 'moment';
import { runInAction, reaction, IReactionDisposer } from 'mobx';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import {
    C_SCORES_DATE_FORMAT,
    C_TIMELINE_MAX_DATE,
    C_TIMELINE_MIN_DATE,
    MOMENTJS_DATE_DISPLAY_FORMAT,
} from '../../constants';
import {
    IChartData,
    IChartLine,
    ILineSideIndicator,
    E_DOMAIN_Y_SCALE,
    E_CHART_GROUP_IDENTIFIERS,
    IChartCircle,
    IClosestChartLine,
} from './chartInterfaces';
import { E_RISK_TYPE } from '../../services/lib/APIServiceInterfaces';
import GQPopupStorage from '../GQPopup/GQPopupStorage';
import annoStore from '../../stores/AnnotationsStore';
import { sortedArrayInsertIndex } from '../../utils/generalUtils';
import numeral from 'numeral';
import {
    pick,
    isEqual,
    get,
    filter,
    includes,
    isEmpty,
    map,
    size,
    first,
    isNil,
} from 'lodash/fp';
import { MACROECONOMICS_INDICATORS, MACROECONOMICS_YAXIS_LABEL_MAP } from '../../constants';
import { debounce, throttle } from 'lodash';
import { IActiveCountryMap } from 'interfaces';
import { getRiskLongName } from 'utils/riskUtils';
import { translateGlobalIndex } from 'utils/renderUtils';
import { ChartStore, COMPOSITE_DATA_INTERVAL, minRanges } from './ChartStore';
import { FGIcons, FGStack, FGTypography } from '@fg/design-system';
import rootStore  from '../../RootStore';
import { inject, observer } from 'mobx-react';

export enum Y_AXIS_LABELS {
    Pulse = 'Pulse (Below 0 = Falling  |  Above 0 = Rising)',
    Composite = 'Composite (Low 0  →  High 100)',
    Currency = 'Currency',
    Commodity = 'Commodity',
    Macroeconomics = 'Macroeconomics',
}

const UPDATE_SCALES_KEYS: Array<keyof Props> = [
    'minX',
    'maxX',
    'width',
    'height',
    'isDragging',
    'hasToolbar',
];

const EDITOR_DATE_TOOLTIP = 'insight_editor_date';
const MIN_PIXEL_PADDING_BETWEEN_CIRCLES = 60;

const ECONIMIC_INDICATORS_DATA: E_CHART_GROUP_IDENTIFIERS[] = [
    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
    E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX,
];

const Y_AXIS_NUM_TICKS = 10;


type Props = {
    activeView: string;
    data: IChartData[];
    minX: moment.Moment;
    maxX: moment.Moment;
    width: number;
    height: number;
    emitter: any;
    virtualToday: moment.Moment;
    showVirtualToday: boolean;
    showLines: boolean;
    showTooltip: boolean;
    isDragging: boolean;
    hasToolbar: boolean;
    onVirualTodayChange: (date: moment.Moment) => void;
    onWheel: (isUp: boolean) => void;
    onPan: (range: number) => void;
    onDragChange: (isDragging: boolean) => void;
    annoMode?: boolean;
    changeDataWhenDragStop?: boolean;
    countries?: IActiveCountryMap;
    isDimensionsMultiSelect: boolean;
    compositeDataInterval?: COMPOSITE_DATA_INTERVAL;
    showRiskMarkersData?: boolean;
    chartStore?: ChartStore;
    isExportComponent?: boolean;
}

const xaxisHeight = 20;

type ITexts = Array<{
    key: string;
    yValue: number;
    yPosition: number;
    stroke: string;
    label: string;
}>;

type HoverData = {
    lineIdentifier: string;
    chartGroupIdentifier: E_CHART_GROUP_IDENTIFIERS;
    country: number;
    entityId: number;
    entityType: E_RISK_TYPE;
    indicator: string;
    mouseX: number;
    mouseY: number;
    lineY: number;
    lineColor: string;
    date: moment.Moment;
}

type State = {
    pos: { x: number; date: moment.Moment };
    dragging: boolean;
    rel: { x: number };
    minX: moment.Moment;
    maxX: moment.Moment;
    insightIndicator: {
        x1: number;
        y1: number;
        y2: number;
    };
    xScale: d3.ScaleTime<number, number>;
    yScales: {
        [identifier: string]: d3.ScaleLinear<number, number>;
    };
    hoverInfo: HoverData;
    riskMarkerRect: { radius: number; color: string };
}

const yAxisTickFormatter = (value: number) => {
    let format;
    switch (true) {
        case value < 0.001:
            format = '0,0.[0000]a';
            break;
        case value >= 0.001 && value < 0.01:
            format = '0,0.[000]a';
            break;
        case value >= 0.01 && value < 10:
            format = '0,0.[00]a';
            break;
        default:
            format = '0,0.[0]a';
    }
    return numeral(value).format(format);
};
const VIRTUAL_TODAY_VALUES_CONTAINER = {
    margin: 10,
    minWidth: 100,
    maxWidth: 300,
    charWidth: 7,
    fixedPadding: 64,
}
const PADDING = { LEFT: 50, TOP: 12, BOTTOM: 12, RIGHT: 0 };
const RIGHT_AXIS_TICK_WIDTH = 35;
const RIGHT_AXIS_LABEL_MARGIN = 12;
const RIGHT_AXIS_LABEL_GAP = 4;
const SINGLE_AXIS_BLOCK =
    RIGHT_AXIS_TICK_WIDTH +
    RIGHT_AXIS_LABEL_MARGIN +
    RIGHT_AXIS_LABEL_MARGIN +
    RIGHT_AXIS_LABEL_GAP;

@inject('chartStore')
@observer
export default class Chart extends React.Component<Props, State> {
    private xAxisRef: SVGGElement = null;
    private yAxisRef: SVGGElement = null;
    private externalDatayAxisRef: SVGGElement = null;
    private indicatorsYAxisRef: SVGGElement = null;
    private svgRef: SVGElement = null;
    private updateScalesThrottle: any;
    private startOfDay = moment().startOf('day');
    private insightHoverDebounce: any = null;
    private chartHoverThrottle: any = null;
    private riskMarkerDisposer: IReactionDisposer = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            pos: { x: 0, date: null },
            dragging: false,
            rel: null,
            minX: null,
            maxX: null,
            insightIndicator: {
                x1: null,
                y1: null,
                y2: null,
            },
            yScales: {},
            xScale: null,
            hoverInfo: null,
            riskMarkerRect: null,
        };
        this.updateScalesThrottle = throttle(this.updateScales, 33);
        this.insightHoverDebounce = debounce(this.insightHover.bind(this), 10);
        this.chartHoverThrottle = throttle(this.handleChartHover, 50);
    }

    public componentDidMount() {
        if (this.xAxisRef) this.renderXAxis();
        if (this.yAxisRef) this.renderYAxis();
        if (this.externalDatayAxisRef) this.renderExternalDataYAxis();
        if (this.indicatorsYAxisRef) this.renderIndicatorsYAxis();
        this.updateScales(null);
        // Clear the selected risk-hotspot marker (and its events feed / vertical
        // highlight) when the underlying risk or country is no longer selected.
        this.riskMarkerDisposer = reaction(
            () => {
                const marker = rootStore.chartStore.riskMarkerEvent;
                if (!marker) return true;
                const riskStore = rootStore.risksStore.storesByRiskType?.get(
                    marker.riskType
                );
                const riskSelected = riskStore
                    ? riskStore.getValues().includes(marker.riskId)
                    : false;
                const countrySelected = rootStore.countryStore.currentCountryList.includes(
                    marker.countryId
                );
                return riskSelected && countrySelected;
            },
            stillValid => {
                if (!stillValid) {
                    runInAction(() => {
                        rootStore.chartStore.riskMarkerEvent = null;
                    });
                }
            }
        );
    }

    public componentWillUnmount() {
        if (this.riskMarkerDisposer) {
            this.riskMarkerDisposer();
            this.riskMarkerDisposer = null;
        }
        if (!this.props.isExportComponent) {
            runInAction(() => {
                rootStore.chartStore.riskMarkerEvent = null;
            });
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (this.props.annoMode && !nextProps.annoMode) {
            GQPopupStorage.addData(EDITOR_DATE_TOOLTIP, null);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        // When a drag (e.g. the horizontal range slider) just ended, bypass the
        // throttle and force a synchronous scale recompute. Otherwise the
        // pending trailing call may be skipped/coalesced and state.xScale can
        // remain at an intermediate value while props.minX/maxX have already
        // reached the extreme edge, causing lines (e.g. Past Projection) to be
        // drawn off-screen until the next prop change.
        if (prevProps.isDragging && !this.props.isDragging) {
            this.updateScalesThrottle.cancel();
            this.updateScales(prevProps);
        } else {
            this.updateScalesThrottle(prevProps);
        }
        if (!this.props.showRiskMarkersData && prevProps.showRiskMarkersData) {
            this.setState({ riskMarkerRect: null });
        }
    }

    private updateScales = (prevProps: Props) => {
        if (this.xAxisRef) this.renderXAxis();
        if (this.yAxisRef) this.renderYAxis();
        if (this.externalDatayAxisRef) this.renderExternalDataYAxis();
        if (this.indicatorsYAxisRef) this.renderIndicatorsYAxis();
        const previous = pick(UPDATE_SCALES_KEYS, prevProps);
        const current = pick(UPDATE_SCALES_KEYS, this.props);
        // comparing the 'data' prop is expensive,
        // so if one of the other props is not equal -
        // there is no need to compare the 'data' prop
        if (
            isEqual(current, previous) &&
            isEqual(get('data', this.props), get('data', prevProps))
        ) {
            return;
        }

        const { data } = this.props;
        const yScales: {} = {};
        data.forEach(datum => {
            const shared = this.isSharedDomain(datum);
            let scale: d3.ScaleLinear<number, number> = null;
            if (shared) {
                scale = this.getYScale(datum, 0);
                yScales[datum.identifier] = scale;
            } else {
                datum.lines.forEach((line, i) => {
                    scale = this.getYScale(datum, i);
                    yScales[`${datum.identifier}_${line.identifier}`] = scale;
                });
            }
        });
        const xScale = this.getXScale();
        this.setState({ yScales, xScale });
        runInAction(() => {
            annoStore.yScales = yScales;
            annoStore.xScale = xScale;
            // This fixes calling xScale.invert() not being defined,
            //  and allowing us to still use makeAutoObservable() in AnnotationStore.
            // Without this call - mobx thinks that xScale is a mobx action since it's a function
            annoStore.xScale.invert = xScale.invert;
        });
        this.startOfDay = moment().startOf('day');
    };

    public onMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
        this.props.onDragChange(false);
        setTimeout(() => {
            this.setState({
                dragging: false,
                pos: { x: 0, date: null },
            });
        }, 0);
    };

    public onMouseMove = (e: MouseEvent) => {
        if (!this.state.dragging) {
            this.setState({
                dragging: true,
            });
        }
        const relativeX = e.pageX - this.state.rel.x;
        const [rangeMin, rangeMax] = this.state.xScale.range();
        const relativeWidth = relativeX / (rangeMax - rangeMin);
        const daysDiff = this.state.maxX.diff(this.state.minX, 'days');
        const newMinx = this.state.minX
            .clone()
            .add((daysDiff * relativeWidth).toFixed(2), 'days');
        const relativeOldDate = (
            this.state.pos.date || this.state.minX
        ).clone();
        const range = newMinx.diff(relativeOldDate, 'days');
        relativeOldDate.add(range, 'days');
        this.props.onPan(-1 * range);

        this.setState({
            pos: {
                x: e.pageX - this.state.rel.x,
                date: relativeOldDate,
            },
        });
    };

    public onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
        if (e.button !== 0) {
            return;
        }
        this.setState(
            {
                rel: {
                    x: e.pageX,
                },
                minX: this.props.minX.clone(),
                maxX: this.props.maxX.clone(),
            },
            () => {
                document.addEventListener('mousemove', this.onMouseMove);
                document.addEventListener('mouseup', this.onMouseUp);
            }
        );
        this.props.onDragChange(true);
    };

    private draggableNodeRef  = React.createRef<any>();

    public render() {
        if (!this.state.xScale || this.isLoading()) {
            return null;
        }
        const { data } = this.props;
        const texts: ITexts = [];
        const sideIndicators: ILineSideIndicator[] = [];
        const actualTodayX = this.state.xScale(this.startOfDay);
        const yScaleLabels = this.getYScaleLabels();

        const [, xRangeEnd] = this.state.xScale.range();
        const onVirtualTodayDrag =
            this.props.compositeDataInterval === COMPOSITE_DATA_INTERVAL.MONTHLY
                ? this.onVirtualTodayDragMonthly
                : this.onVirtualTodayDrag; 
        const onVirtualTodayDragStops =
            this.props.compositeDataInterval ===
            COMPOSITE_DATA_INTERVAL.MONTHLY
                ? this.onVirtualTodayDragStopsMonthly
                : this.onVirtualTodayDragStops;
        const isInsufficientDateRange =
            this.props.compositeDataInterval ===
                COMPOSITE_DATA_INTERVAL.MONTHLY &&
            this.props.maxX.diff(this.props.minX, 'months', true) <= 2;
        return (
            <div
                data-testid="risk-chart"
                className="gq-chart-container"
                onMouseMove={this._insightHover}>
                {isInsufficientDateRange && (
                    <div className="gq-chart-insufficient-range-overlay">
                        <FGStack alignItems="center" direction="column">
                            <FGIcons icon="InformationFilled" size={60} />
                            <FGTypography variant="headline3" tag="h3">
                                Insufficient date range
                            </FGTypography>
                            <FGTypography
                                cssStyle={{ whiteSpace: 'nowrap' }}
                                variant="body3-short"
                                tag="label"
                                color="weak">
                                Expand date range to at least two months to see
                                monthly changes.
                            </FGTypography>
                        </FGStack>
                    </div>
                )}
                {this.props.annoMode && (
                    <>
                        <div
                            className="gq-chart-insight-indicator gq-insight-hover-bar"
                            style={{
                                width: 1,
                                left: this.state.insightIndicator.x1,
                                top: Math.min(
                                    this.state.insightIndicator.y1,
                                    this.state.insightIndicator.y2
                                ),
                                height: Math.abs(
                                    this.state.insightIndicator.y2 -
                                        this.state.insightIndicator.y1
                                ),
                            }}
                        />
                        <div
                            className="gq-chart-insight-indicator gq-inight-hover-dot"
                            style={{
                                width: 5,
                                height: 5,
                                left: this.state.insightIndicator.x1 - 2.5,
                                top: this.state.insightIndicator.y1 - 2.5,
                            }}
                        />
                    </>
                )}
                <div className="gq-chart-svg-container">
                    <div className="y-axis-label">{first(yScaleLabels)}</div>
                    <svg
                        className={cx([
                            'gq-chart',
                            {
                                'no-pointer-events': this.props.annoMode,
                            },
                        ])}
                        width="100%"
                        height="100%"
                        onWheel={this.onMouseWheel}
                        onMouseDown={this.onMouseDown}
                        onMouseMove={this.onChartHoverEnter}
                        onMouseLeave={this.onChartHoverLeave}
                        ref={el => (this.svgRef = el)}
                        data-dragging={this.state.dragging}>
                        <g
                            className="xAxis"
                            transform={`translate(0,${this.props.height -
                                xaxisHeight})`}
                            ref={el => (this.xAxisRef = el)}
                        />
                        <g
                            className="yAxis"
                            ref={el => (this.yAxisRef = el)}
                            transform={`translate(${PADDING.LEFT}, 0)`}
                        />
                        {this.renderExternalYAxisLabels(yScaleLabels)}
                        {this.state.riskMarkerRect &&
                            this.props.chartStore.riskMarkerEvent &&
                            this.renderRiskMarkerHighlight()}

                        {!isInsufficientDateRange &&
                            data.map(itemData =>
                                this.drawChartGroups(
                                    itemData,
                                    texts,
                                    sideIndicators,
                                    this.state.hoverInfo
                                )
                            )}
                        {actualTodayX >= PADDING.LEFT &&
                            !this.startOfDay.isSame(
                                this.props.virtualToday,
                                'day'
                            ) && (
                                <g
                                    className="chart-actual-today"
                                    transform={`translate(${actualTodayX},0)`}>
                                    <rect
                                        className="actual-today-bar"
                                        height={Math.max(
                                            0,
                                            this.props.height - xaxisHeight
                                        )}
                                        y={0}
                                    />
                                    <text
                                        textAnchor="middle"
                                        className="chart-actual-today-text"
                                        dy={20}
                                        dx={
                                            this.props.hasToolbar
                                                ? -(
                                                      this.props.height -
                                                      xaxisHeight -
                                                      30
                                                  )
                                                : -(this.props.height / 2)
                                        }>
                                        Today
                                    </text>
                                </g>
                            )}
                        {!this.props.showRiskMarkersData &&
                            this.state.hoverInfo && (
                                <circle
                                    className="chart-hover-dot"
                                    cx={this.state.hoverInfo.mouseX}
                                    cy={this.state.hoverInfo.lineY}
                                    r={5}
                                    fill={this.state.hoverInfo.lineColor}
                                    stroke="#fff"
                                    strokeWidth={2}
                                    style={{ pointerEvents: 'none' }}
                                />
                            )}
                        {this.props.showVirtualToday &&
                            !isInsufficientDateRange &&
                            (() => {
                                if (isEmpty(texts)){
                                    this.populatePulseTexts(texts);
                                }
                                const vtWidth = this.getVirtualTodayContainerWidth(texts);
                                const isLeftSide =
                                    this.state.xScale(this.props.virtualToday) >
                                    xRangeEnd - vtWidth;
                                return (
                                <Draggable
                                    /* 
                                draggableNodeRef is required to suppress the warning
                                https://stackoverflow.com/questions/63603902/finddomnode-is-deprecated-in-strictmode-finddomnode-was-passed-an-instance-of-d
                                */
                                    nodeRef={this.draggableNodeRef}
                                    axis="x"
                                    bounds={{
                                        left: this.state.xScale(
                                            this.props.minX
                                        ),
                                        right: this.state.xScale(
                                            this.props.maxX
                                        ),
                                        top: 0,
                                        bottom: 0,
                                    }}
                                    onDrag={onVirtualTodayDrag}
                                    onStop={onVirtualTodayDragStops}
                                    position={{
                                        x: this.state.xScale(
                                            this.props.virtualToday
                                        ),
                                        y: 0,
                                    }}>
                                    <g
                                        ref={this.draggableNodeRef}
                                        className="chart-virtual-today"
                                        transform={`translate(${this.state.xScale(
                                            this.props.virtualToday
                                        )},0)`}>
                                        <rect
                                            className="virtual-today-bar"
                                            height={Math.max(
                                                0,
                                                this.props.height - xaxisHeight
                                            )}
                                            y={0}
                                        />
                                        {this.props.showTooltip && (
                                            <g className="virtual-today-values-container">
                                                <foreignObject
                                                    x={
                                                        isLeftSide
                                                            ? -(
                                                                vtWidth +
                                                                VIRTUAL_TODAY_VALUES_CONTAINER.margin
                                                            )
                                                            : VIRTUAL_TODAY_VALUES_CONTAINER.margin
                                                    }
                                                    y={0}
                                                    width={
                                                        vtWidth
                                                    }
                                                    height={48 + 24 * size(texts)}>
                                                    <div
                                                        //@ts-ignore
                                                        xmlns="http://www.w3.org/1999/xhtml"
                                                        style={{
                                                            backgroundColor:
                                                                'var(--fg-color-panel-dark)',
                                                            borderRadius: 3,
                                                        }}>
                                                        <FGTypography
                                                            variant="captions"
                                                            tag="label"
                                                            cssStyle={{
                                                                paddingLeft: 8,
                                                                color: 'var(--fg-color-text-white) !important',
                                                            }}>
                                                            {this.props.virtualToday.format(
                                                                "MMM D, 'YY"
                                                            )}
                                                        </FGTypography>
                                                        {map(
                                                            text => (
                                                                <div
                                                                    key={text.key}
                                                                    className="virtual-today-values-item-container">
                                                                    <div className="virtual-today-values-left-side">
                                                                        <div
                                                                            className="virtual-today-values-stroke-box"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    text.stroke,
                                                                            }}
                                                                        />
                                                                        <div className="virtual-today-values-item-label">
                                                                            {
                                                                                text.label
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <div className="virtual-today-values-right-side">
                                                                        <div className="virtual-today-values-separator" />
                                                                        <div className="virtual-today-values-item-value">
                                                                            {typeof text.yValue ===
                                                                                'number' &&
                                                                                text.yValue.toFixed(
                                                                                    2
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ),
                                                            texts
                                                        )}
                                                    </div>
                                                </foreignObject>
                                            </g>
                                        )}
                                        
                                        <g
                                            className="virtual-today-pin-group"
                                            id="inspectorRect"
                                            transform={`translate(0, ${this
                                                .props.height / 2})`}>
                                            <rect
                                                className="virtual-today-pin-outer-bar"
                                                x="-3.5px"
                                                width="7px"
                                                height="15px"
                                            />
                                            <rect
                                                className="virtual-today-pin-inner-bar"
                                                width="1px"
                                                height="9px"
                                                x="1.25px"
                                                y="3"
                                            />
                                            <rect
                                                className="virtual-today-pin-inner-bar"
                                                width="1px"
                                                height="9px"
                                                x="-1.25px"
                                                y="3"
                                            />
                                        </g>
                                        <rect
                                            data-testid="chart-date-handle"
                                            className="virtual-today-dragArea"
                                            height={Math.max(
                                                0,
                                                this.props.height
                                            )}
                                            width="10"
                                            transform="translate(-5,0)"
                                        />
                                    </g>
                                </Draggable>
                            );
                            })()}
                    </svg>
                    {this.state.hoverInfo &&
                        !this.props.isExportComponent &&
                        !this.props.showRiskMarkersData &&
                        this.renderHoverTooltip()}
                    {this.state.hoverInfo &&
                        !this.props.isExportComponent &&
                        this.props.showRiskMarkersData &&
                        this.renderRiskMarkersTooltip()}
                </div>
            </div>
        );
    }

    private renderRiskMarkerHighlight() {
        const rect = this.state.riskMarkerRect;
        const event = this.props.chartStore.riskMarkerEvent;
        if (!event || !event.date) {
            return null;
        }
        const eventDate = moment(event.date, C_SCORES_DATE_FORMAT);
        if (!eventDate.isValid()) {
            return null;
        }
        if (
            eventDate.isBefore(this.props.minX) ||
            eventDate.isAfter(this.props.maxX)
        ) {
            return null;
        }
        const circleX = this.state.xScale(eventDate);
        const [xRangeStart, xRangeEnd] = this.state.xScale.range();
        if (circleX < xRangeStart || circleX > xRangeEnd) {
            return null;
        }
        const chartH = this.props.height - xaxisHeight;
        const padding = 4;
        return (
            <rect
                key={`${event.riskType}-${event.riskId}-${event.countryId}-${event.date}`}
                className="risk-marker-highlight"
                x={circleX - rect.radius - padding}
                y={0}
                width={rect.radius * 2 + padding * 2}
                height={chartH}
                fill={rect.color}
                opacity={0.15}
                style={{
                    pointerEvents: 'none',
                    transformOrigin: `0 ${chartH / 2}px`,
                    animation: 'riskHighlightExpand 0.25s ease-out forwards',
                }}
            />
        );
    }

    private renderExternalYAxisLabels(yScaleLabels: Y_AXIS_LABELS[]) {
        const { data } = this.props;
        if (size(data) <= 1) {
            return null;
        }
        const rightEdge = this.state.xScale.range()[1];
        const chartMidY = (this.props.height - xaxisHeight) / 2;
        const labelOffset =
            RIGHT_AXIS_TICK_WIDTH + RIGHT_AXIS_LABEL_MARGIN;
        const secondAxisOffset = labelOffset + RIGHT_AXIS_LABEL_MARGIN + RIGHT_AXIS_LABEL_GAP;
        return (
            <>
                <g
                    className="externalDatayAxis"
                    ref={el => (this.externalDatayAxisRef = el)}
                    transform={`translate(${rightEdge}, 0)`}
                />
                <text
                    className="chart-right-axis-label"
                    transform={`translate(${rightEdge +
                        labelOffset}, ${chartMidY}) rotate(-90)`}
                    textAnchor="middle"
                    dominantBaseline="central">
                    {size(yScaleLabels) > 1 && yScaleLabels[1]}
                </text>
                {size(data) > 2 && (
                    <>
                        <g
                            className="indicatorsYAxis"
                            ref={el => (this.indicatorsYAxisRef = el)}
                            transform={`translate(${rightEdge +
                                secondAxisOffset}, 0)`}
                        />
                        <text
                            className="chart-indicator-axis-label"
                            fill={this.getIndicatorsLineColor()}
                            transform={`translate(${rightEdge +
                                secondAxisOffset +
                                labelOffset}, ${chartMidY}) rotate(-90)`}
                            textAnchor="middle"
                            dominantBaseline="central">
                            {size(yScaleLabels) > 2 && yScaleLabels[2]}
                        </text>
                    </>
                )}
            </>
        );
    }

    private onChartHoverEnter = (e: React.MouseEvent<SVGSVGElement>) => {
        if (this.props.annoMode || this.props.isExportComponent || this.state.dragging) {
            return;
        }
        e.persist();
        this.chartHoverThrottle(e);
    };

    private handleChartHover = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!this.svgRef || !this.state.xScale) return;
        const svgRect = this.svgRef.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;
        const [chartLeft, chartRight] = this.state.xScale.range();
        if (
            mouseX < chartLeft ||
            mouseX > chartRight
        ) {
            this.onChartHoverLeave();
            return;
        }
        let date = moment(this.state.xScale.invert(mouseX));
        const isMonthly =
            this.props.compositeDataInterval ===
            COMPOSITE_DATA_INTERVAL.MONTHLY;
        if (isMonthly) {
            date = date.clone().endOf('month').startOf('day');
        }
        const dateKey = date.format(C_SCORES_DATE_FORMAT);
        const { data } = this.props;
        //todo: DRY with getClosestLine
        let closest: {
            distance: number;
            lineId: string;
            groupId: E_CHART_GROUP_IDENTIFIERS;
            country: number | null;
            lineY: number;
            lineColor: string;
            entityId: number | null;
            entityType: E_RISK_TYPE | null;
            indicator: string | null;
        } | null = null;
        data.forEach(datum => {
            const shared = this.isSharedDomain(datum);
            datum.lines.forEach((line, i) => {
                const identifier = shared
                    ? datum.identifier
                    : `${datum.identifier}_${line.identifier}`;
                const yScale = this.state.yScales[identifier];
                if (!yScale) return;
                const val = line.getValue(dateKey);
                if (!val) return;
                // For Pulse lines, ChartLine.getValue() carries the previous
                // value forward when the date isn't in the data set. Skip
                // pulse candidates whose value's date doesn't match the
                // hovered date so the pulse line can't become the "closest"
                // line beyond its actual data range.
                if (
                    datum.identifier === E_CHART_GROUP_IDENTIFIERS.PULSE &&
                    val.date &&
                    val.date.format(C_SCORES_DATE_FORMAT) !== dateKey
                ) {
                    return;
                }
                // getValue() carries the last value forward, so skip lines
                // whose data ends before the hovered date (e.g. indicators /
                // past projection) to avoid a unwanted tooltip past their end.
                if (
                    line.lastDataDate &&
                    date.isAfter(line.lastDataDate, 'day')
                ) {
                    return;
                }
                const lineY = yScale(val.yValue);
                const dist = Math.abs(lineY - mouseY);
                if (!closest || dist < closest.distance) {
                    closest = {
                        distance: dist,
                        lineId: line.identifier,
                        groupId: datum.identifier,
                        country: line.country,
                        lineY,
                        lineColor: (line.props.stroke as string) || '#fff',
                        entityId: line.extraData?.entityId,
                        entityType: line.extraData?.entityType,
                        indicator: line.extraData?.indicator || null,
                    };
                }
            });
        });

        if (!closest || closest.distance >= 30) {
                this.setState({ hoverInfo: null });
                return;
        }
        const snappedX = isMonthly ? this.state.xScale(date) : mouseX;
        if (isMonthly && Math.abs(mouseX - snappedX) > 30) {
            this.setState({ hoverInfo: null });
            return;
        }
        this.setState({
            hoverInfo: {
                lineIdentifier: closest.lineId,
                chartGroupIdentifier: closest.groupId,
                country: closest.country,
                entityId: closest.entityId,
                entityType: closest.entityType,
                indicator: closest.indicator,
                mouseX: snappedX,
                mouseY,
                lineY: closest.lineY,
                lineColor: closest.lineColor,
                date,
            },
        });
    };

    private onChartHoverLeave = () => {
        this.setState({ hoverInfo: null });
    };

    private getTooltipValues(): Array<{ label: string; value: string; color: string }> {
        const { hoverInfo } = this.state;
        if (!hoverInfo) return [];
        const dateKey = hoverInfo.date.format(C_SCORES_DATE_FORMAT);
        const { data } = this.props;
        const values: Array<{
            label: string;
            value: string;
            color: string;
        }> = [];
        const isIndicatorLine = this.isIndicatorLine(
            hoverInfo.chartGroupIdentifier
        );
        if (isIndicatorLine) {
            data.forEach(datum => {
                if (datum.identifier !== hoverInfo.chartGroupIdentifier) return;
                datum.lines.forEach(line => {
                    if (line.identifier !== hoverInfo.lineIdentifier) return;
                    const val = line.getValue(dateKey);
                    let label: string;
                    if (datum.identifier === E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE) {
                        const country = this.props.countries?.[line.country];
                        label = country?.currency_code || country?.name || 'Currency';
                    } else {
                        const indicator = line.extraData?.indicator;
                        const indicatorLabel = translateGlobalIndex[indicator] || indicator;
                        if (indicator && MACROECONOMICS_INDICATORS.includes(indicator)) {
                            const country = this.props.countries?.[line.country];
                            const countryName = country?.name || country?.abbreviation || '';
                            label = countryName ? `${indicatorLabel} - ${countryName}` : indicatorLabel;
                        } else {
                            label = indicatorLabel;
                        }
                    }
                    values.push({
                        label,
                        value: val ? val.yValue.toFixed(2) : 'N/A',
                        color: line.props.stroke as string || '#fff',
                    });
                });
            });
        } else {
            const targetCountry = hoverInfo.country;
            const targetEntityId = hoverInfo.entityId;
            data.forEach(datum => {
                if (includes(datum.identifier, [
                    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
                    E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX,
                ])) return;
                datum.lines.forEach(line => {
                    if (line.country !== targetCountry) return;
                    if (targetEntityId != null) {
                        const lineEntityId = line.extraData?.entityId;
                        if (lineEntityId !== targetEntityId) return;
                    }
                    const val = line.getValue(dateKey);
                    //todo: use labels dictionary
                    let label: string;
                    if (datum.identifier === E_CHART_GROUP_IDENTIFIERS.PULSE) {
                        // Hide Pulse tooltip row past the end of pulse data.
                        // ChartLine.getValue() carries the previous value
                        // forward when the date isn't in the data set, so
                        // require the value's date to match the hovered date.
                        if (
                            !val?.date ||
                            val.date.format(C_SCORES_DATE_FORMAT) !== dateKey
                        ) {
                            return;
                        }
                        label = 'Pulse';
                    //todo: refactor by making this more clear - add a isProjection or 
                    // check line type to be Projection which currently failds
                    } else if (line.hideYValue) {
                        label = 'Past Projection';
                    } else {
                        label = 'Composite';
                    }
                    values.push({
                        label,
                        value: val ? val.yValue.toFixed(2) : 'N/A',
                        color: line.props.stroke as string || '#fff',
                    });
                });
            });
        }
        return values;
    }

    private isIndicatorLine(identifier: E_CHART_GROUP_IDENTIFIERS): boolean {
        return includes(identifier, [
            E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
            E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX,
        ]);
    }

    private getTooltipHeader(): string {
        const { hoverInfo } = this.state;
        if (!hoverInfo) return null;
        const isIndicatorLine = this.isIndicatorLine(
            hoverInfo.chartGroupIdentifier
        );
        if (isIndicatorLine) {
            if (
                hoverInfo.chartGroupIdentifier ===
                E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
            ) {
                return 'Currency';
            }
            const indicator = hoverInfo.indicator;
            if (indicator && MACROECONOMICS_INDICATORS.includes(indicator)) {
                return 'Macroeconomics';
            }
            return 'Commodity';
        }
        if (this.props.isDimensionsMultiSelect) {
            return getRiskLongName(hoverInfo.entityType, hoverInfo.entityId);
        }
        if (
            hoverInfo.country != null &&
            this.props.countries?.[hoverInfo.country]
        ) {
            return (
                this.props.countries[hoverInfo.country].name ||
                this.props.countries[hoverInfo.country].abbreviation
            );
        }
        return null;
    }
    private renderRiskMarkersTooltip(){
        const { hoverInfo } = this.state;
        if (!hoverInfo) return null;
        //todo: refactor. this was copy-pasted from renderHoverTooltip()
        const tooltipWidth = 180;
        const tooltipPadding = 32;
        let left = hoverInfo.mouseX + tooltipPadding;
        let top = hoverInfo.mouseY - tooltipPadding;
        if (left + tooltipWidth > this.props.width) {
            left = hoverInfo.mouseX - tooltipWidth - tooltipPadding;
        }
        if (top < 0) {
            top = tooltipPadding;
        }
                return (
                    <div
                        className="risk-marker-tooltip"
                        style={{
                            width: tooltipWidth,
                            left,
                            top,
                        }}>
                        <FGTypography
                            variant="body4"
                            tag="label"
                            color="strong">
                            Marker highlights events that drive large high-frequency
                            scores that can influence Composite Risk.
                            Click on the dot to review related events.
                        </FGTypography>
                    </div>
                );

    }
    private renderHoverTooltip(){
        const { hoverInfo } = this.state;
        if (!hoverInfo) return null;
        const tooltipValues = this.getTooltipValues();
        if (isEmpty(tooltipValues)) return null;
        const header = this.getTooltipHeader();
        const maxValueLength = tooltipValues.reduce(
            (max, item) => Math.max(max, (item.label?.length || 0) + (item.value?.length || 0)),
            0
        );
        const tooltipWidth = Math.max(
            120,
            Math.min(
                300,
                Math.max(
                    (header?.length || 0) * 5 + 64,
                    maxValueLength * 5 + 64
                )
            )
        );
        const tooltipPadding = 32;
        let left = hoverInfo.mouseX + tooltipPadding;
        let top = hoverInfo.mouseY - tooltipPadding;
        if (left + tooltipWidth > this.props.width) {
            left = hoverInfo.mouseX - tooltipWidth - tooltipPadding;
        }
        if (top < 0) {
            top = tooltipPadding;
        }
        return (
            <div
                className="chart-line-tooltip"
                style={{
                    width: tooltipWidth,
                    left,
                    top,
                }}>
                <FGStack direction="column" gap={4}>
                    <FGTypography variant="captions" tag="label">
                        {hoverInfo.date.format("MMM D, 'YY")}
                    </FGTypography>
                    <FGStack gap={8} alignItems="center">
                        <div
                            style={{
                                width: 8,
                                height: 8,
                                backgroundColor: hoverInfo.lineColor,
                            }}
                        />
                        <FGTypography
                            variant="body3-long"
                            tag="label"
                            cssStyle={{ color: 'var(--fg-color-text-white)' }}>
                            {this.getTooltipHeader()}
                        </FGTypography>
                    </FGStack>
                    <FGStack direction="column" gap={4}>
                        {tooltipValues.map((item, index) => (
                            <FGStack alignItems="center" key={index} gap={8}>
                                <FGTypography
                                    variant="body4"
                                    tag="label"
                                    cssStyle={{
                                        color: 'var(--fg-color-text-white)',
                                    }}>
                                    {item.label}
                                </FGTypography>
                                <div className="chartline-tooltip-values-separator" />
                                <FGTypography
                                    cssStyle={{
                                        color: 'var(--fg-color-text-white)',
                                    }}
                                    variant="body4"
                                    tag="label">
                                    {item.value}
                                </FGTypography>
                            </FGStack>
                        ))}
                    </FGStack>
                </FGStack>
            </div>
        );
    }

    private getXScale() {
        const xScale = d3.scaleTime();
        const rightPad =
            PADDING.RIGHT +
            SINGLE_AXIS_BLOCK * Math.max(0, size(this.props.data) - 1);
        xScale
            .range([PADDING.LEFT, this.props.width - rightPad])
            .domain([this.props.minX, this.props.maxX]);
        return xScale;
    }

    private getYScale(itemData: IChartData, lineIndex: number) {
        const yScale = d3.scaleLinear();
        yScale.range([
            this.props.height - xaxisHeight - PADDING.BOTTOM,
            PADDING.TOP,
        ]);

        const scale = itemData.domainY.expanded;
        if (scale === E_DOMAIN_Y_SCALE.shared) {
            yScale.domain([itemData.minValue, itemData.maxValue]);
        } else {
            yScale.domain([
                itemData.lines[lineIndex].minValue,
                itemData.lines[lineIndex].maxValue,
            ]);
        }

        return yScale;
    }

    private isVTInRange(date: moment.Moment): boolean {
        return (
            date.isSameOrAfter(this.props.minX) &&
            date.isSameOrBefore(this.props.maxX)
        );
    }

    private getChartXFromEvent(e: DraggableEvent): number | null {
        if (!this.svgRef) return null;
        const clientX = get(['touches', 0, 'clientX'], e) ?? get('clientX', e);
        if (isNil(clientX)) return null;
        const svgRect = this.svgRef.getBoundingClientRect();
        return clientX - svgRect.left;
    }

    private onVirtualTodayDragMonthly = (e: DraggableEvent, data: DraggableData) => {
        e.stopPropagation();
        if (this.props.changeDataWhenDragStop) {
            return;
        }
        const xPos = this.getChartXFromEvent(e);
        if (isNil(xPos)) {
            return;
        }
        const newVToday = moment(this.state.xScale.invert(xPos))
            .endOf('month')
            .startOf('day');
        if (this.isVTInRange(newVToday)) {
            this.props.onVirualTodayChange(newVToday);
        }
    };
    private onVirtualTodayDragStopsMonthly = (
        e: DraggableEvent,
        data: DraggableData
    ) => {
        if (!this.props.changeDataWhenDragStop) {
            return;
        }
        const xPos = this.getChartXFromEvent(e);
        if (isNil(xPos)){
            return;
        }
        const rawDate = moment(this.state.xScale.invert(xPos));
        const daysInMonth = rawDate.daysInMonth();
        const midpoint = Math.ceil(daysInMonth / 2);
        const newVToday = rawDate.date() > midpoint
            ? rawDate.clone().endOf('month').startOf('day')
            : rawDate.clone().startOf('month').startOf('day');
        if (this.isVTInRange(newVToday)) {
            this.props.onVirualTodayChange(newVToday);
        }
    };

    private onVirtualTodayDrag = (e: DraggableEvent, data: DraggableData) => {
        e.stopPropagation();
        if (this.props.changeDataWhenDragStop) {
            return;
        }
        const newVToday = moment(this.state.xScale.invert(data.lastX));
        if (this.isVTInRange(newVToday)) {
            this.props.onVirualTodayChange(newVToday);
        }
    };
    private onVirtualTodayDragStops = (
        e: DraggableEvent,
        data: DraggableData
    ) => {
        if (!this.props.changeDataWhenDragStop) {
            return;
        }
        const scale = this.state.xScale;
        let newVToday = moment(scale.invert(data.lastX));
        if (newVToday.hour() >= 12) {
            newVToday = newVToday.startOf('day').add(1, 'day');
        } else {
            newVToday = newVToday.startOf('day');
        }
        if (this.isVTInRange(newVToday)) {
            this.props.onVirualTodayChange(newVToday);
        }
    };

    private onMouseWheel = (e: React.WheelEvent<SVGSVGElement>) => {
        if (e.deltaX !== 0) {
            const [rMin, rMax] = this.state.xScale.range();
            const relativeWidth = e.deltaX / (rMax - rMin);
            const pan = Math.round(
                relativeWidth * this.props.maxX.diff(this.props.minX, 'days')
            );
            this.props.onPan(pan);
            // else because we dont want both and we prefer x
        } else if (e.deltaY !== 0) {
            this.props.onWheel(e.deltaY < 0);
        }
    };

    private isLoading() {
        return this.props.width === null;
    }

    private isSharedDomain(datum: IChartData) {
        return (
            datum.domainY.expanded === E_DOMAIN_Y_SCALE.shared
        );
    }

    private isLineHighlighted(line: IChartLine, itemData: IChartData, hoverInfo: HoverData){
        if (!hoverInfo) return true;
        const isIndicatorHover = this.isIndicatorLine(hoverInfo.chartGroupIdentifier);
        const isIndicatorLine = this.isIndicatorLine(itemData.identifier);
        if (isIndicatorHover) {
            return (
                line.identifier === hoverInfo.lineIdentifier &&
                itemData.identifier === hoverInfo.chartGroupIdentifier
            );
        }
        if (isIndicatorLine) return false;
        if (
            this.props.isDimensionsMultiSelect &&
            hoverInfo.entityId != null
        ) {
            const lineEntityId = line.extraData?.entityId;
            return lineEntityId === hoverInfo.entityId;
        }
        return line.country === hoverInfo.country;
    }

    private drawChartGroups(
        itemData: IChartData,
        texts: ITexts,
        sideIndicators: ILineSideIndicator[],
        hoverInfo: HoverData
    ) {
        const shared = this.isSharedDomain(itemData);

        return (
            <g className="line-container" key={itemData.identifier}>
                {itemData.lines.map((line, i) => {
                    const yScale = this.state.yScales[
                        shared
                            ? itemData.identifier
                            : `${itemData.identifier}_${line.identifier}`
                    ];
                    const xScale = this.state.xScale;
                    if (!yScale || !xScale) {
                        return null;
                    }
                    const highlighted = this.isLineHighlighted(
                        line,
                        itemData,
                        hoverInfo
                    );
                    const lineOpacity = hoverInfo && !highlighted ? 0.5 : 1;
                    return (
                        <g
                            key={line.identifier}
                            className="chart-group"
                            style={{
                                opacity: lineOpacity,
                            }}>
                            {this.props.showLines && (
                                <path
                                    strokeWidth={2}
                                    className={`chart-line ${line.className ||
                                        ''}`}
                                    d={line.draw(yScale, xScale)}
                                    {...line.props}
                                    stroke={line.props.stroke}
                                    style={{ pointerEvents: 'none' }}
                                />
                            )}
                            {!line.hideYValue && line.chartType && this.populateText(
                                line,
                                texts,
                                yScale,
                                line.props.stroke
                            )}
                            {this.props.compositeDataInterval ===
                                COMPOSITE_DATA_INTERVAL.MONTHLY && (
                                <g className="chart-group-circles-monthly">
                                    {this.renderCirclesMonthly(line, yScale)}
                                </g>
                            )}
                            {this.props.showRiskMarkersData && (
                                <g className="chart-group-circles-risk-markers">
                                    {this.renderLineCirclesByZoom(line, yScale)}
                                </g>
                            )}
                            {line.sideIndicator &&
                                sideIndicators.push({
                                    componentProps: {
                                        isRight: true,
                                        x: -24, //todo: check
                                        y:
                                            yScale(
                                                get(
                                                    'yValue',
                                                    line.getValue(
                                                        this.props.maxX.format(
                                                            'YYYY-MM-DD'
                                                        )
                                                    )
                                                )
                                            ) - 4,
                                        ...line.sideIndicator.componentProps,
                                    },
                                }) &&
                                undefined}
                        </g>
                    );
                })}
            </g>
        );
    }
    private _insightHover = (e: React.MouseEvent<HTMLDivElement>) => {
        e.persist();
        this.insightHoverDebounce(e);
    };

    private insightHover = (e: React.MouseEvent<HTMLDivElement>) => {
        const { clientX, clientY } = e;
        if (!this.props.annoMode || !this.svgRef) {
            return;
        }
        const svgRect = this.svgRef.getBoundingClientRect();
        GQPopupStorage.addData(EDITOR_DATE_TOOLTIP, {
            element: null,
            fixedPos: {
                left: clientX,
                top: clientY,
            },
            options: {
                noDiamond: true,
            },
            tooltipData: (
                <div className="gq-insight-editor-date-tooltip">
                    {moment(
                        this.state.xScale.invert(clientX - svgRect.left)
                    ).format(MOMENTJS_DATE_DISPLAY_FORMAT)}
                </div>
            ),
        });
        const closestLine = this.getClosestLine(
            clientX - svgRect.left,
            clientY - svgRect.top
        );
        if (!closestLine) {
            return;
        }
        runInAction(() => {
            annoStore.closestLine = closestLine;
        });
        this.setState({
            insightIndicator: {
                x1: clientX - svgRect.left,
                y2: clientY - svgRect.top,
                y1: this.state.yScales[closestLine.identifier](
                    closestLine.yVal
                ),
            },
        });
    };

    private getClosestLine(x: number, y: number): IClosestChartLine {
        if (isEmpty(this.state.yScales) || !this.state.xScale) {
            return null;
        }
        const { data } = this.props;

        const inputNormalizer = d3
            .scaleLinear()
            .range([this.props.height - xaxisHeight, 0])
            .domain([0, 100]);
        const date = moment.min(
            C_TIMELINE_MAX_DATE,
            moment.max(C_TIMELINE_MIN_DATE, moment(this.state.xScale.invert(x)))
        );
        const nextDay = date
            .clone()
            .startOf('day')
            .add(1, 'days');
        let inputY = inputNormalizer.invert(y);
        inputY = Math.max(0, Math.min(inputY, 100));
        let dObj: IClosestChartLine = null;
        data.forEach(datum => {
            const shared = this.isSharedDomain(datum);
            datum.lines.forEach((line, i) => {
                if (
                    !includes(line.chartType, [
                        E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK,
                        E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK,
                    ])
                ) {
                    return;
                }

                const identifier = shared
                    ? datum.identifier
                    : `${datum.identifier}_${line.identifier}`;
                const scale = this.state.yScales[identifier];
                const val = {
                    ...line.getValue(date.format(C_SCORES_DATE_FORMAT)),
                };
                const nextDayVal = line.getValue(
                    nextDay.format(C_SCORES_DATE_FORMAT)
                );
                if (isEmpty(nextDayVal) || isEmpty(val)) {
                    return;
                }

                const delta = nextDayVal.yValue - val.yValue;
                const diff = date.diff(date.clone().startOf('day'), 'minutes');
                val.yValue += delta * (diff / (24 * 60));
                inputNormalizer.domain(scale.range());
                inputNormalizer.range([0, 100]);
                const normalized = inputNormalizer(scale(val.yValue));
                const distance = Math.abs(normalized - inputY);
                if (!dObj || distance < dObj.distance) {
                    dObj = {
                        distance,
                        yVal: val.yValue,
                        identifier,
                        activeDate: date,
                        type: datum.identifier,
                        country: line.country,
                    };
                }
            });
        });
        return dObj;
    }

    private calcCircleMonthlyRadiusScale(){
        const dayRange = this.props.maxX.diff(this.props.minX, 'days');
        const MIN_DAYS = 60;
        const MAX_DAYS = C_TIMELINE_MAX_DATE.diff(C_TIMELINE_MIN_DATE, 'days');
        const MIN_SCALE = 0.2;
        const MAX_SCALE = 1.0;
        const t = Math.max(
            0,
            Math.min(1, (dayRange - MIN_DAYS) / (MAX_DAYS - MIN_DAYS))
        );
        const radiusScale = MAX_SCALE - t * (MAX_SCALE - MIN_SCALE);
        return radiusScale;
    }
    private renderCirclesMonthly(
        line: IChartLine,
        yScale: d3.ScaleLinear<number, number>
    ): JSX.Element[] {
        const xScale = this.state.xScale;
        const [xRangeMin, xRangeMax] = xScale.range();
        const radiusScale = this.calcCircleMonthlyRadiusScale();
        return line.allValues
            .filter(val => {
                if (!val.circle) return false;
                const cx = xScale(val.date);
                return (
                    cx >= xRangeMin && cx <= xRangeMax 
                );
            })
            .map(val => (
                <circle
                    key={val.circle.identifier}
                    cx={xScale(val.date)}
                    cy={yScale(val.yValue)}
                    r={val.circle.radius * radiusScale}
                    fill={val.circle.props.fill}
                    className={val.circle.className}
                    data-date={val.date.format('YYYY-MM-DD')}
                />
            ));
    }
    
    private renderLineCirclesByZoom(
        line: IChartLine,
        yScale: d3.ScaleLinear<number, number>
    ): JSX.Element[] {
        const circlesByDate: {
            [dateStr: string]: IChartCircle;
        } = {};
        const xScale = this.state.xScale;
        const circlesByImpact: {
            [impact: number]: {
                [dateStr: string]: IChartCircle;
            };
        } = {};
        for (
            const curDate = this.props.minX.clone().startOf('day');
            curDate.isSameOrBefore(this.props.maxX);
            curDate.add(1, 'days')
        ) {
            const dateStr = curDate.format(C_SCORES_DATE_FORMAT);
            const curCircle = line.getValue(dateStr)?.circle;
            if (curCircle) {
                const roundedImpact = Math.round(curCircle.impact * 100) / 100;
                if (!circlesByImpact[roundedImpact]) {
                    circlesByImpact[roundedImpact] = {};
                }
                circlesByImpact[roundedImpact][dateStr] = curCircle;
            }
        }
        const datesWithCirclesSorted: string[] = [];
        Object.keys(circlesByImpact)
            .sort()
            .reverse()
            .forEach(circleImpact => {
                const impactCirclesByDate =
                    circlesByImpact[Number(circleImpact)];
                _.forEach(impactCirclesByDate, (circle, dateStr) => {
                    const nextDateIndex = sortedArrayInsertIndex(
                        datesWithCirclesSorted,
                        dateStr
                    );
                    const nextDate = moment(
                        datesWithCirclesSorted[nextDateIndex]
                    );
                    const prevDate = moment(
                        datesWithCirclesSorted[nextDateIndex - 1]
                    );
                    const closestPrevCircle =
                        circlesByDate[prevDate.format(C_SCORES_DATE_FORMAT)];
                    const closestNextCircle =
                        circlesByDate[nextDate.format(C_SCORES_DATE_FORMAT)];
                    let canAdd = true;
                    if (closestPrevCircle && closestPrevCircle !== circle) {
                        const deltaX =
                            xScale(prevDate) -
                            xScale(line.getValue(dateStr).date);
                        const deltaY =
                            yScale(
                                line.getValue(
                                    prevDate.format(C_SCORES_DATE_FORMAT)
                                ).yValue
                            ) - yScale(line.getValue(dateStr).yValue);
                        const distanceBetweenCircles = Math.sqrt(
                            deltaX ** 2 + deltaY ** 2
                        );
                        if (
                            distanceBetweenCircles <
                            MIN_PIXEL_PADDING_BETWEEN_CIRCLES
                        ) {
                            canAdd = false;
                        }
                    }
                    if (
                        canAdd &&
                        closestNextCircle &&
                        closestNextCircle !== circle
                    ) {
                        const deltaX =
                            xScale(nextDate) -
                            xScale(line.getValue(dateStr).date);
                        const deltaY =
                            yScale(
                                line.getValue(
                                    nextDate.format(C_SCORES_DATE_FORMAT)
                                ).yValue
                            ) - yScale(line.getValue(dateStr).yValue);
                        const distanceBetweenCircles = Math.sqrt(
                            deltaX ** 2 + deltaY ** 2
                        );
                        if (
                            distanceBetweenCircles <
                            MIN_PIXEL_PADDING_BETWEEN_CIRCLES
                        ) {
                            canAdd = false;
                        }
                    }
                    if (canAdd) {
                        circlesByDate[dateStr] = circle;
                        datesWithCirclesSorted.splice(
                            nextDateIndex,
                            0,
                            dateStr
                        );
                    }
                });
            });
        const res: JSX.Element[] = [];
        _.forEach(circlesByDate, (circle, dateStr) => {
            if (circle) {
                res.push(
                    <circle
                        key={circle.identifier}
                        className={
                            circle.className
                                ? `chart-circle-risk-marker ${circle.className}`
                                : 'chart-circle-risk-marker'
                        }
                        cx={xScale(line.getValue(dateStr).date)}
                        cy={yScale(line.getValue(dateStr).yValue)}
                        r={circle.radius}
                        {...circle.props}
                        onClick={() =>
                            this.onRiskMarkerClick(
                                line.extraData.entityType,
                                line.extraData.entityId,
                                line.country,
                                dateStr,
                                xScale(line.getValue(dateStr).date),
                                circle.radius,
                                line.props.stroke as string
                            )
                        }
                    />
                );
            }
        });
        return res;
    }

    //note: This is a new risk marker implementation based on Shai's suggestion.
    // The code is just added for now as a reference - it hasn't been reviewed yet.
    //@ts-ignore
    private renderLineCirclesByZoomNew(
        line: IChartLine,
        yScale: d3.ScaleLinear<number, number>
    ): JSX.Element[] {
        const xScale = this.state.xScale;
        if (!xScale) return [];

        // 1. Collect all candidate circles in the visible range
        interface CircleCandidate {
            dateStr: string;
            circle: IChartCircle;
            impact: number;
            x: number;
            y: number;
        }

        const allCandidates: CircleCandidate[] = [];
        for (
            const curDate = this.props.minX.clone().startOf('day');
            curDate.isSameOrBefore(this.props.maxX);
            curDate.add(1, 'days')
        ) {
            const dateStr = curDate.format(C_SCORES_DATE_FORMAT);
            const lineValue = line.getValue(dateStr);
            const curCircle = lineValue?.circle;
            if (curCircle) {
                allCandidates.push({
                    dateStr,
                    circle: curCircle,
                    impact: curCircle.impact ?? 0,
                    x: xScale(lineValue.date),
                    y: yScale(lineValue.yValue),
                });
            }
        }

        if (allCandidates.length === 0) return [];

        // Sort all candidates by impact descending
        allCandidates.sort((a, b) => b.impact - a.impact);

        // OLD #2 - Determine max circles that can fit
        // const [rangeMin, rangeMax] = xScale.range();
        // const availableWidth = Math.abs(rangeMax - rangeMin);
        // const maxCircles = Math.max(
        //     1,
        //     Math.floor(availableWidth / MIN_PIXEL_PADDING_BETWEEN_CIRCLES)
        // );

        // 2. Pick top X candidates, keep the rest as replacements
        const MAX_RISK_MARKERS = 7;
        const selected: CircleCandidate[] = allCandidates.slice(
            0,
            Math.min(MAX_RISK_MARKERS, allCandidates.length)
        );
        const remaining: CircleCandidate[] = allCandidates.slice(
            selected.length
        );

        // 4. Resolve collisions iteratively
        // Uses sorted insertion + neighbor-only checks for efficiency
        const getDistance = (a: CircleCandidate, b: CircleCandidate) => {
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            return Math.sqrt(dx * dx + dy * dy);
        };

        let hasCollision = true;
        while (hasCollision) {
            hasCollision = false;
            // Sort selected by x position for spatial neighbor checks
            selected.sort((a, b) => a.x - b.x);

            for (let i = 0; i < selected.length - 1; i++) {
                const curr = selected[i];
                const next = selected[i + 1];
                const distance = getDistance(curr, next);

                if (distance < MIN_PIXEL_PADDING_BETWEEN_CIRCLES) {
                    // Drop the one with the lower impact
                    const dropIndex =
                        curr.impact >= next.impact ? i + 1 : i;
                    selected.splice(dropIndex, 1);

                    // Replace with the next best candidate from remaining
                    if (remaining.length > 0) {
                        selected.push(remaining.shift());
                    }

                    hasCollision = true;
                    break; // restart collision check from the beginning
                }
            }
        }

        // 5. Render surviving circles
        return selected.map(item => (
            <circle
                key={item.circle.identifier}
                className={
                    item.circle.className
                        ? `chart-circle-risk-marker ${item.circle.className}`
                        : 'chart-circle-risk-marker'
                }
                cx={item.x}
                cy={item.y}
                r={item.circle.radius}
                {...item.circle.props}
                onClick={() =>
                    this.onRiskMarkerClick(
                        line.extraData.entityType,
                        line.extraData.entityId,
                        line.country,
                        item.dateStr,
                        item.x,
                        item.circle.radius,
                        line.props.stroke as string
                    )
                }
            />
        ));
    }

    private onRiskMarkerClick(
        riskType: E_RISK_TYPE,
        riskId: number,
        countryId: number,
        date: string,
        cx: number,
        radius: number,
        color: string){
        rootStore.chartStore.riskMarkerEvent = {
            riskType,
            riskId,
            countryId,
            date,
        };
        this.setState({ riskMarkerRect: { radius, color } });
    }

    private getVirtualTodayContainerWidth(texts: ITexts): number {
        const maxLabelLen = texts.reduce(
            (max, text) => Math.max(max, size(text.label)),
            0
        );
        const { charWidth, fixedPadding, minWidth, maxWidth } = VIRTUAL_TODAY_VALUES_CONTAINER;
        const computed = maxLabelLen * charWidth + fixedPadding;
        return Math.max(minWidth, Math.min(maxWidth, computed));
    }

    private getChartLineMarkerLabel(chartLine: IChartLine) {
        const maxLen = 40;
        let label;
        if (this.props.isDimensionsMultiSelect) {
            label = getRiskLongName(
                chartLine?.extraData?.entityType,
                chartLine?.extraData?.entityId
            );
        } else {
            label = this.props.countries[chartLine.country].name;
        }
        if (size(label) < maxLen) {
            return label;
        }
        return label.slice(0, maxLen) + '...';
    }
    private populatePulseTexts(texts: ITexts) {
        this.props.data.forEach(itemData => {
            if (itemData.identifier !== E_CHART_GROUP_IDENTIFIERS.PULSE) return;
            const shared = this.isSharedDomain(itemData);
            itemData.lines.forEach(line => {
                const yScale = this.state.yScales[
                    shared
                        ? itemData.identifier
                        : `${itemData.identifier}_${line.identifier}`
                ];
                if (!yScale) return;
                this.populateText(line, texts, yScale, line.props.stroke as string);
            });
        });
    }

    private populateText(lineData: IChartLine, texts: ITexts, yScale: any, stroke: string) {
        const dataForDate = lineData.getValue(
            this.props.virtualToday.format('YYYY-MM-DD')
        );

        if (dataForDate && dataForDate.yValue) {
            texts.push({
                key: `${lineData.identifier}_expanded_value`,
                yValue: dataForDate.yValue,
                yPosition: yScale(dataForDate.yValue),
                stroke,
                label: this.getChartLineMarkerLabel(lineData),
            });
        }
    }

    private getXAxis(
        fromDate: moment.Moment,
        toDate: moment.Moment,
        xScale: d3.ScaleTime<any, any>
    ) {
        const xAxis = d3.axisBottom(xScale);
        const months = toDate.diff(fromDate, 'months', true);
        const weeks = toDate.diff(fromDate, 'weeks', true);
        let format: (date: Date) => string = d3.timeFormat("%b '%y");
        switch (true) {
            case weeks <= 3:
                xAxis.ticks(d3.timeDay.every(2));
                format = d3.timeFormat("%b %d, '%y");
                break;
            case months < 2:
                xAxis.ticks(d3.timeWeek.every(1));
                format = d3.timeFormat("%b %d, '%y");
                break;
            case months < 4:
                xAxis.ticks(d3.timeWeek.every(2));
                format = d3.timeFormat("%b %d, '%y");
                break;
            case months < 10:
                xAxis.ticks(d3.timeMonth.every(1));
                break;
            case months < 18:
                xAxis.ticks(d3.timeMonth.every(3));
                break;
            case months < 36:
                xAxis.ticks(d3.timeMonth.every(6));
                break;
            case months > 36:
                format = d3.timeFormat('%Y');
                xAxis.ticks(d3.timeYear.every(1));
                break;
        }
        //@ts-ignore
        //https://stackoverflow.com/questions/55992937/typescript-error-when-using-d3-timeformat-in-axis-tickformat
        xAxis.tickFormat(format);
        return xAxis;
    }

    private renderXAxis = () => {
        const xScale = this.state.xScale;
        // If the date range is degenerate (e.g. during a very fast drag where
        // minX and maxX collapse), d3 produces NaN tick values. Skip rendering
        // entirely rather than show corrupt labels.
        if (
            !this.props.minX ||
            !this.props.maxX ||
            !this.props.minX.isValid() ||
            !this.props.maxX.isValid()
        ) {
            return;
        }
        if (
            this.props.maxX.diff(this.props.minX, 'days') <=
                minRanges.rangeLeft + minRanges.rangeRight &&
            this.props.isDragging
        ) {
            //claude - see how i made it less than or equal minRanges.rangeLeft + minRanges.rangeRight
            d3.select(this.xAxisRef)
                .selectAll('*')
                .remove();
            return;
        }
        const xAxis = this.getXAxis(this.props.minX, this.props.maxX, xScale);
        d3.select(this.xAxisRef).call(xAxis);
    };

    private getLineDataByTypes = ()=>{
        const { data } = this.props;
        const riskData = filter(
            data =>
                data.identifier === E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK,
            data
        );
        const crData = filter(
            data =>
                data.identifier === E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK,
            data
        );
        const pulseData = filter(
            data => data.identifier === E_CHART_GROUP_IDENTIFIERS.PULSE,
            data
        );
        const economicData = filter(
            data => includes(data.identifier, ECONIMIC_INDICATORS_DATA),
            data
        );
        const currencyData = filter(
            data =>
                includes(
                    data.identifier,
                    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
                ),
            data
        );
        const commodityData = filter(
            data =>
                includes(
                    data.identifier,
                    E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX
                ),
            data
        );
        //todo: verify code
        const macroLine = commodityData
            .flatMap(data => data.lines)
            .find(line => MACROECONOMICS_INDICATORS.includes(line.extraData?.indicator));

        return {
            riskData,
            pulseData,
            economicData,
            crData,
            currencyData,
            commodityData,
            macroLine,
        };
    }

    private macroYAxisLabel(macroLine: IChartLine) {
        if (!macroLine) return null;
        const indicator = macroLine.extraData?.indicator;
        const label = indicator === MACROECONOMICS_INDICATORS[0] ?
            MACROECONOMICS_YAXIS_LABEL_MAP.real_gdp_growth :
            MACROECONOMICS_YAXIS_LABEL_MAP.cpi_inflation;
        const country = this.props.countries[macroLine.country]?.abbreviation;
        return `${label} - ${country}`;
    }

    private getYScaleLabels = () => {
        const {
            riskData,
            pulseData,
            currencyData,
            commodityData,
            macroLine,
            crData,
        } = this.getLineDataByTypes();
        const labels = [];
        if (!isEmpty(riskData)) {
            labels.push(Y_AXIS_LABELS.Composite);
        }
        if (!isEmpty(crData)) {
            labels.push(Y_AXIS_LABELS.Composite);
        }
        if (!isEmpty(pulseData)) {
            labels.push(Y_AXIS_LABELS.Pulse);
        }
        if (!isEmpty(currencyData)) {
            const firstLine = currencyData?.[0]?.lines?.[0];
            const country = this.props.countries[firstLine?.country];
            const code = country?.currency_code;
            const pair = code
                ? code === 'USD'
                    ? 'EUR:USD'
                    : `USD:${code}`
                : '';
            labels.push(
                pair
                    ? `${Y_AXIS_LABELS.Currency} (${pair})`
                    : Y_AXIS_LABELS.Currency
            );
        }
        const macroLabel = this.macroYAxisLabel(macroLine);
        if (macroLabel) labels.push(macroLabel);
        if (!macroLabel && !isEmpty(commodityData)){
            const indicatorLabel = translateGlobalIndex[commodityData[0].lines[0].extraData.indicator];
            labels.push(`${Y_AXIS_LABELS.Commodity} - ${indicatorLabel}`);
        }

        return labels.slice(0, 3);
    }

    private renderYAxis = () => {
        const yScale = d3.scaleLinear();
        yScale.range([this.props.height - xaxisHeight, 0]);
        const yAxis = d3.axisLeft(yScale);
        //@ts-ignore
        //https://stackoverflow.com/questions/55992937/typescript-error-when-using-d3-timeformat-in-axis-tickformat
        yAxis.tickFormat(yAxisTickFormatter);
        yAxis.ticks(Y_AXIS_NUM_TICKS);
        yAxis.scale(yScale);
        const [rangeStart, rangeEnd] = this.getXScale().range();
        yAxis.tickSizeInner(-(rangeEnd - rangeStart));
        const {riskData, pulseData, economicData, crData} = this.getLineDataByTypes();
        switch (true) {
            case !isEmpty(crData):
                yScale.domain([crData[0].minValue, crData[0].maxValue]);
                break;
            case !isEmpty(riskData):
                yScale.domain([riskData[0].minValue, riskData[0].maxValue]);
                break;
            case isEmpty(riskData) && !isEmpty(pulseData):
                yScale.domain([pulseData[0].minValue, pulseData[0].maxValue]);
                break;
            case isEmpty(riskData) &&
                isEmpty(pulseData) &&
                !isEmpty(economicData):
                yScale.domain([
                    economicData[0].minValue,
                    economicData[0].maxValue,
                ]);
                break;
        }

        d3.select(this.yAxisRef).call(yAxis);
    };
    private renderExternalDataYAxis = () => {
        const yScale = d3.scaleLinear();
        yScale.range([this.props.height - xaxisHeight, 0]);
        const yAxis = d3.axisRight(yScale);
        //@ts-ignore
        //https://stackoverflow.com/questions/55992937/typescript-error-when-using-d3-timeformat-in-axis-tickformat
        yAxis.tickFormat(yAxisTickFormatter);
        // shai - this value was 7 before i changed it,
        // is there any good reason why it wasn't 10 ? who not be exactly the same as y axis?
        yAxis.ticks(Y_AXIS_NUM_TICKS);
        yAxis.scale(yScale);
        yAxis.tickSizeInner(3);
        const {riskData, pulseData, economicData, crData} = this.getLineDataByTypes();
        switch (true) {
            case !isEmpty(crData) && !isEmpty(pulseData):
                yScale.domain([pulseData[0].minValue, pulseData[0].maxValue]);
                break;
            case !isEmpty(riskData) && !isEmpty(pulseData):
                yScale.domain([pulseData[0].minValue, pulseData[0].maxValue]);
                break;
            case isEmpty(pulseData) && !isEmpty(economicData):
            case isEmpty(riskData) &&
                !isEmpty(pulseData) &&
                !isEmpty(economicData):
                yScale.domain([
                    economicData[0].minValue,
                    economicData[0].maxValue,
                ]);
                break;
        }
        d3.select(this.externalDatayAxisRef).call(yAxis);
    };

    private renderIndicatorsYAxis = () => {
        const {
            economicData,
            crData,
            pulseData,
            riskData,
        } = this.getLineDataByTypes();
        if (!isEmpty(crData) && !isEmpty(riskData) && !isEmpty(pulseData)) {
            return;
        }
        if (isEmpty(economicData)) {
            return;
        }
        const yScale = d3.scaleLinear();
        yScale.range([this.props.height - xaxisHeight, 0]);
        const yAxis = d3.axisRight(yScale);
        //@ts-ignore
        yAxis.tickFormat(yAxisTickFormatter);
        yAxis.ticks(Y_AXIS_NUM_TICKS);
        yAxis.scale(yScale);
        yAxis.tickSizeInner(3);
        if (!isEmpty(economicData)) {
            yScale.domain([economicData[0].minValue, economicData[0].maxValue]);
        }
        const axisEl = d3.select(this.indicatorsYAxisRef);
        axisEl.call(yAxis);
        const color = this.getIndicatorsLineColor();
        if (color) {
            axisEl.selectAll('.tick text').style('fill', color);
            axisEl.selectAll('.tick line').style('stroke', color);
        }
    };

    private getIndicatorsLineColor = (): string | null => {
        const { economicData } = this.getLineDataByTypes();
        if (isEmpty(economicData)) return null;
        return economicData[0]?.lines?.[0]?.props?.stroke;
    };
}
